import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useSubscription } from '../../hooks/useSubscription';
import { useAccessContext } from '../../hooks/useApi';
import { Building2, Users2, Home, Target } from 'lucide-react';
import './styles.css';
import logo from '../../images/inline_eternavaults-logo.png'
import NotificationBell from '../NotificationBell';
import MobileSignupModal from '../MobileSignupModal';
import { FaBars, FaTimes } from 'react-icons/fa';

// Helper function to check if a path is active
const isActivePath = (currentPath, linkPath) => {
  if (linkPath === '/') return currentPath === '/';
  return currentPath.startsWith(linkPath);
};

export default function Navigation() {
  const { isAuthenticated, loginWithRedirect, logout, user, isLoading, getAccessTokenSilently } = useAuth0();
  const { hasActiveSubscription } = useSubscription();
  const { data: accessContext } = useAccessContext();
  const capabilities = accessContext?.capabilities || {};
  const location = useLocation();
  const navigate = useNavigate();

  // All state declarations must come before any useEffect that uses them
  const [isMobile, setIsMobile] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isBurgerOpen, setIsBurgerOpen] = useState(false);
  const [isEternaVaultsOpen, setIsEternaVaultsOpen] = useState(false);
  const [isSolutionsOpen, setIsSolutionsOpen] = useState(false);
  const [isHowItWorksOpen, setIsHowItWorksOpen] = useState(false);
  const [isTrustSecurityOpen, setIsTrustSecurityOpen] = useState(false);
  const [isResourcesOpen, setIsResourcesOpen] = useState(false);
  const [isPortalAccessOpen, setIsPortalAccessOpen] = useState(false);
  const [showMobileSignupModal, setShowMobileSignupModal] = useState(false);
  const [planName, setPlanName] = useState(null);
  const [member, setMember] = useState(null);
  const [isPartner, setIsPartner] = useState(false);

  // All refs must come after state but before useEffect
  const dropdownRef = React.useRef(null);
  const burgerMenuRef = React.useRef(null);
  const moreDropdownRef = React.useRef(null);
  const eternaVaultsRef = React.useRef(null);
  const solutionsRef = React.useRef(null);
  const howItWorksRef = React.useRef(null);
  const trustSecurityRef = React.useRef(null);
  const resourcesRef = React.useRef(null);
  const portalAccessRef = React.useRef(null);

  // Set member email and Auth0 ID immediately from Auth0 user if available
  useEffect(() => {
    if (user?.email || user?.sub) {
      setMember({
        email: user.email,
        sub: user.sub,
        auth0Id: user.sub
      });
    }
  }, [user?.email, user?.sub]);

  const foundingCircle = location.pathname.includes('founding-circle');
  const isPartnerRoute = location.pathname.startsWith('/partner');
  const isLocal = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

  // Check if user is a partner
  useEffect(() => {
    const checkPartnerStatus = async () => {
      if (!isAuthenticated || isLoading) return;

      try {
        const token = await getAccessTokenSilently({
          audience: process.env.REACT_APP_AUTH0_AUDIENCE || 'https://api.eternavaults.com'
        });

        // Decode JWT to check roles
        const payload = JSON.parse(atob(token.split('.')[1]));
        const roles = payload['https://eternavaults.com/roles'] || [];

        setIsPartner(roles.includes('partner_admin'));
      } catch (err) {
        console.error('Error checking partner status:', err);
        setIsPartner(false);
      }
    };

    checkPartnerStatus();
  }, [isAuthenticated, isLoading, getAccessTokenSilently]);

  // Fetch plan information and member email to check feature access
  useEffect(() => {
    const fetchPlanInfo = async () => {
      if (!isAuthenticated || !hasActiveSubscription || isPartner) {
        return;
      }
      try {
        const token = await getAccessTokenSilently({
          audience: "https://api.eternavaults.com"
        });
        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.eternavaults.com';

        // Fetch plan name
        const usageResponse = await fetch(`${API_BASE_URL}/api/dashboard/storage-usage`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        if (usageResponse.ok) {
          const data = await usageResponse.json();
          setPlanName(data.plan || 'Legacy');
        }

        // Member email should already be set from user.email in useEffect above
        // But fetch from API as fallback if not set
        if (!user?.email) {
          const memberResponse = await fetch(`${API_BASE_URL}/api/members/me`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          if (memberResponse.ok) {
            const memberData = await memberResponse.json();
            setMember({ email: memberData.email });
          }
        }
      } catch (err) {
        console.error('Error fetching plan info:', err);
        setPlanName('Legacy'); // Default to Legacy on error
      }
    };
    fetchPlanInfo();
  }, [isAuthenticated, hasActiveSubscription, getAccessTokenSilently, isPartner]);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (eternaVaultsRef.current && !eternaVaultsRef.current.contains(event.target)) {
        setIsEternaVaultsOpen(false);
      }
      if (solutionsRef.current && !solutionsRef.current.contains(event.target)) {
        setIsSolutionsOpen(false);
      }
      if (howItWorksRef.current && !howItWorksRef.current.contains(event.target)) {
        setIsHowItWorksOpen(false);
      }
      if (trustSecurityRef.current && !trustSecurityRef.current.contains(event.target)) {
        setIsTrustSecurityOpen(false);
      }
      if (resourcesRef.current && !resourcesRef.current.contains(event.target)) {
        setIsResourcesOpen(false);
      }
      if (portalAccessRef.current && !portalAccessRef.current.contains(event.target)) {
        setIsPortalAccessOpen(false);
      }
    }
    if (isDropdownOpen || isEternaVaultsOpen || isSolutionsOpen || isHowItWorksOpen || isTrustSecurityOpen || isResourcesOpen || isPortalAccessOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen, isEternaVaultsOpen, isSolutionsOpen, isHowItWorksOpen, isTrustSecurityOpen, isResourcesOpen]);

  // Close burger menu on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (burgerMenuRef.current && !burgerMenuRef.current.contains(event.target)) {
        setIsBurgerOpen(false);
      }
    }
    if (isBurgerOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isBurgerOpen]);

  // Mobile navigation tabs
  const allMobileTabs = [
    { path: '/member-selection', icon: '🏠', label: 'Access Vaults', requiresAuth: true, requiresSubscription: true, isSelection: true },
    { path: '/dashboard', icon: '📊', label: 'Dashboard', requiresAuth: true, requiresSubscription: true },
    { path: '/gallery', icon: '📸', label: 'Gallery', requiresAuth: true, requiresSubscription: true },
    { path: '/family-tree', icon: '👨‍👩‍👧‍👦', label: 'Family', requiresAuth: true, requiresSubscription: true, feature: 'familyTree' },
    { path: '/privacy-folder', icon: '🔒', label: 'Privacy', requiresAuth: true, requiresSubscription: true },
    { path: '/message-center', icon: '💬', label: 'Messages', requiresAuth: true, requiresSubscription: true, feature: 'messageCenter' },
    { path: '/emergency-access', icon: '🚨', label: 'Emergency', requiresAuth: true, requiresSubscription: true, feature: 'emergencyAccess' },
    { path: '/family-continuity-structure', icon: '⚙️', label: 'Settings', requiresAuth: true, requiresSubscription: true }
  ];

  // Filter tabs based on capabilities
  const mobileTabs = allMobileTabs.filter(tab => {
    if (tab.feature) {
      return capabilities[tab.feature] === true;
    }
    return true; // Show tab if no feature requirement
  });

  // Check if user is on a specific member's dashboard
  const isOnMemberDashboard = location.pathname.includes('/dashboard/');
  const currentMemberId = location.pathname.match(/\/dashboard\/([^\/]+)/)?.[1];

  // Handle signup click - redirect to contact page (membership signup hidden)
  const handleSignupClick = () => {
    window.location.href = '/contact';
  };

  // Handle modal actions (no longer needed but keeping for compatibility)
  const handleModalClose = () => {
    setShowMobileSignupModal(false);
  };

  const handleModalContinue = () => {
    window.location.href = '/contact';
  };

  // Hide navigation entirely on partner routes (PartnerNavBar is used instead)
  if (isPartnerRoute) {
    return null;
  }

  // Desktop navigation
  if (!isMobile) {
    return (
      <nav className="nav-bar">
        <div className="nav-left">
          <Link to="/" className="nav-logo">
            <img className='logo-img' src={logo} alt="Eterna Vaults Logo" />
          </Link>
        </div>
        {!foundingCircle && <div className="nav-links">
          {!isAuthenticated ? (
            // Logged out users - simple navigation
            <>
              {/* EternaVaults dropdown */}
              <div className="nav-dropdown" ref={eternaVaultsRef}>
                <button
                  className="nav-link dropdown-trigger"
                  onClick={() => setIsEternaVaultsOpen(!isEternaVaultsOpen)}
                  aria-expanded={isEternaVaultsOpen}
                >
                  EternaVaults
                  <span className={`dropdown-arrow ${isEternaVaultsOpen ? 'open' : ''}`}>▼</span>
                </button>
                {isEternaVaultsOpen && (
                  <div className="dropdown-menu open">
                    <Link
                      to="/continuity-os"
                      className={`dropdown-item continuity-os-item ${isActivePath(location.pathname, '/continuity-os') ? 'active' : ''}`}
                      onClick={() => setIsEternaVaultsOpen(false)}
                    >
                      EternaVaults Continuity OS™
                    </Link>
                    <Link
                      to="/why-eternavaults"
                      className={`dropdown-item ${isActivePath(location.pathname, '/why-eternavaults') ? 'active' : ''}`}
                      onClick={() => setIsEternaVaultsOpen(false)}
                    >
                      Why Families Choose EternaVaults
                    </Link>
                    <div className="dropdown-divider"></div>
                    <Link
                      to="/pillars-of-continuity"
                      className={`dropdown-item ${isActivePath(location.pathname, '/pillars-of-continuity') ? 'active' : ''}`}
                      onClick={() => setIsEternaVaultsOpen(false)}
                    >
                      Pillars of Continuity™
                    </Link>
                    <Link
                      to="/continuity-architecture"
                      className={`dropdown-item ${isActivePath(location.pathname, '/continuity-architecture') ? 'active' : ''}`}
                      onClick={() => setIsEternaVaultsOpen(false)}
                    >
                      Continuity Architecture™
                    </Link>
                    <Link
                      to="/authority-decision-architecture"
                      className={`dropdown-item ${isActivePath(location.pathname, '/authority-decision-architecture') ? 'active' : ''}`}
                      onClick={() => setIsEternaVaultsOpen(false)}
                    >
                      Authority & Decision Architecture™
                    </Link>
                    <Link
                      to="/continuity-infrastructure"
                      className={`dropdown-item ${isActivePath(location.pathname, '/continuity-infrastructure') ? 'active' : ''}`}
                      onClick={() => setIsEternaVaultsOpen(false)}
                    >
                      Continuity Infrastructure™
                    </Link>
                    <Link
                      to="/temporal-continuity"
                      className={`dropdown-item ${isActivePath(location.pathname, '/temporal-continuity') ? 'active' : ''}`}
                      onClick={() => setIsEternaVaultsOpen(false)}
                    >
                      Temporal Continuity™
                    </Link>
                    <Link
                      to="/continuity-seismic-map"
                      className={`dropdown-item ${isActivePath(location.pathname, '/continuity-seismic-map') ? 'active' : ''}`}
                      onClick={() => setIsEternaVaultsOpen(false)}
                    >
                      The Continuity Seismic Map™
                    </Link>
                    <div className="dropdown-divider"></div>
                    <Link
                      to="/about"
                      className={`dropdown-item ${isActivePath(location.pathname, '/about') ? 'active' : ''}`}
                      onClick={() => setIsEternaVaultsOpen(false)}
                    >
                      About EternaVaults
                    </Link>
                  </div>
                )}
              </div>

              {/* Solutions dropdown */}
              <div className="nav-dropdown" ref={solutionsRef}>
                <button
                  className="nav-link dropdown-trigger"
                  onClick={() => setIsSolutionsOpen(!isSolutionsOpen)}
                  aria-expanded={isSolutionsOpen}
                >
                  Solutions
                  <span className={`dropdown-arrow ${isSolutionsOpen ? 'open' : ''}`}>▼</span>
                </button>
                {isSolutionsOpen && (
                  <div className="dropdown-menu open">
                    <a
                      href="/who-we-work-with"
                      className={`dropdown-item ${isActivePath(location.pathname, '/who-we-work-with') ? 'active' : ''}`}
                      onClick={(e) => {
                        e.preventDefault();
                        window.location.href = '/who-we-work-with';
                      }}
                    >
                      Private Stewardship
                    </a>
                    <Link
                      to="/institutional"
                      className={`dropdown-item ${isActivePath(location.pathname, '/institutional') ? 'active' : ''}`}
                      onClick={() => setIsSolutionsOpen(false)}
                    >
                      Family Offices
                    </Link>
                    <Link
                      to="/for-advisors"
                      className={`dropdown-item ${isActivePath(location.pathname, '/for-advisors') ? 'active' : ''}`}
                      onClick={() => setIsSolutionsOpen(false)}
                    >
                      For Advisors
                    </Link>
                    <Link
                      to="/advisor-pack"
                      className={`dropdown-item ${isActivePath(location.pathname, '/advisor-pack') ? 'active' : ''}`}
                      onClick={() => setIsSolutionsOpen(false)}
                    >
                      Advisors & Fiduciaries
                    </Link>
                    <Link
                      to="/white-label"
                      className={`dropdown-item ${isActivePath(location.pathname, '/white-label') ? 'active' : ''}`}
                      onClick={() => setIsSolutionsOpen(false)}
                    >
                      Custodied Infrastructure
                    </Link>
                  </div>
                )}
              </div>

              {/* How It Works dropdown */}
              <div className="nav-dropdown" ref={howItWorksRef}>
                <button
                  className="nav-link dropdown-trigger"
                  onClick={() => setIsHowItWorksOpen(!isHowItWorksOpen)}
                  aria-expanded={isHowItWorksOpen}
                >
                  How It Works
                  <span className={`dropdown-arrow ${isHowItWorksOpen ? 'open' : ''}`}>▼</span>
                </button>
                {isHowItWorksOpen && (
                  <div className="dropdown-menu open">
                    <Link
                      to="/authority-activation-flow"
                      className={`dropdown-item ${isActivePath(location.pathname, '/authority-activation-flow') ? 'active' : ''}`}
                      onClick={() => setIsHowItWorksOpen(false)}
                    >
                      Authority Activation Flow™
                    </Link>
                    <Link
                      to="/how"
                      className={`dropdown-item ${isActivePath(location.pathname, '/how') ? 'active' : ''}`}
                      onClick={() => setIsHowItWorksOpen(false)}
                    >
                      Continuity Domains™
                    </Link>
                    <Link
                      to="/process"
                      className={`dropdown-item ${isActivePath(location.pathname, '/process') ? 'active' : ''}`}
                      onClick={() => setIsHowItWorksOpen(false)}
                    >
                      Succession & Governance Flow
                    </Link>
                    <Link
                      to="/emergency-access-model"
                      className={`dropdown-item ${isActivePath(location.pathname, '/emergency-access-model') ? 'active' : ''}`}
                      onClick={() => setIsHowItWorksOpen(false)}
                    >
                      Emergency Access Model
                    </Link>
                    <Link
                      to="/temporal-continuity-execution"
                      className={`dropdown-item ${isActivePath(location.pathname, '/temporal-continuity-execution') ? 'active' : ''}`}
                      onClick={() => setIsHowItWorksOpen(false)}
                    >
                      Temporal Execution
                    </Link>
                  </div>
                )}
              </div>

              {/* Trust & Security dropdown */}
              <div className="nav-dropdown" ref={trustSecurityRef}>
                <button
                  className="nav-link dropdown-trigger"
                  onClick={() => setIsTrustSecurityOpen(!isTrustSecurityOpen)}
                  aria-expanded={isTrustSecurityOpen}
                >
                  Trust & Security
                  <span className={`dropdown-arrow ${isTrustSecurityOpen ? 'open' : ''}`}>▼</span>
                </button>
                {isTrustSecurityOpen && (
                  <div className="dropdown-menu open">
                    <Link
                      to="/security"
                      className={`dropdown-item ${isActivePath(location.pathname, '/security') ? 'active' : ''}`}
                      onClick={() => setIsTrustSecurityOpen(false)}
                    >
                      Security Overview
                    </Link>
                    <Link
                      to="/security-transparency"
                      className={`dropdown-item ${isActivePath(location.pathname, '/security-transparency') ? 'active' : ''}`}
                      onClick={() => setIsTrustSecurityOpen(false)}
                    >
                      Privacy & Zero-Knowledge
                    </Link>
                    <Link
                      to="/security"
                      className={`dropdown-item ${isActivePath(location.pathname, '/security') ? 'active' : ''}`}
                      onClick={() => setIsTrustSecurityOpen(false)}
                    >
                      Compliance & Controls
                    </Link>
                    <Link
                      to="/security"
                      className={`dropdown-item ${isActivePath(location.pathname, '/security') ? 'active' : ''}`}
                      onClick={() => setIsTrustSecurityOpen(false)}
                    >
                      Audit & Resilience
                    </Link>
                  </div>
                )}
              </div>

              {/* Resources dropdown */}
              <div className="nav-dropdown" ref={resourcesRef}>
                <button
                  className="nav-link dropdown-trigger"
                  onClick={() => setIsResourcesOpen(!isResourcesOpen)}
                  aria-expanded={isResourcesOpen}
                >
                  Resources
                  <span className={`dropdown-arrow ${isResourcesOpen ? 'open' : ''}`}>▼</span>
                </button>
                {isResourcesOpen && (
                  <div className="dropdown-menu open">
                    <Link
                      to="/journal"
                      className={`dropdown-item ${isActivePath(location.pathname, '/journal') ? 'active' : ''}`}
                      onClick={() => setIsResourcesOpen(false)}
                    >
                      The Legacy Journal
                    </Link>
                    <Link
                      to="/archive"
                      className={`dropdown-item ${isActivePath(location.pathname, '/archive') ? 'active' : ''}`}
                      onClick={() => setIsResourcesOpen(false)}
                    >
                      Digital Heritage Archive
                    </Link>
                    <Link
                      to="/family-legacy-guide"
                      className={`dropdown-item ${isActivePath(location.pathname, '/family-legacy-guide') ? 'active' : ''}`}
                      onClick={() => setIsResourcesOpen(false)}
                    >
                      Family Legacy Guide
                    </Link>
                    <Link
                      to="/founder-musings"
                      className={`dropdown-item ${isActivePath(location.pathname, '/founder-musings') ? 'active' : ''}`}
                      onClick={() => setIsResourcesOpen(false)}
                    >
                      Founder Musings
                    </Link>
                    <Link
                      to="/press"
                      className={`dropdown-item ${isActivePath(location.pathname, '/press') ? 'active' : ''}`}
                      onClick={() => setIsResourcesOpen(false)}
                    >
                      Press & Media
                    </Link>
                  </div>
                )}
              </div>
            </>
          ) : (
            // Logged in users - spread out main features
            // Hide these links if user is a partner (partners use PartnerNavBar instead)
            !isPartner && (
              <>
                {hasActiveSubscription ? (
                  <>
                    <Link to="/member-selection" className='nav-link'>Access Vaults</Link>
                    {capabilities.messageCenter ? (
                      <Link to="/message-center" className='nav-link'>Message Center</Link>
                    ) : (
                      <span
                        className='nav-link nav-link-disabled'
                        style={{ opacity: 0.5, cursor: 'not-allowed', position: 'relative' }}
                      >
                        Message Center
                        <div className="premium-tooltip">
                          <div className="tooltip-icon">👑</div>
                          <div className="tooltip-content">
                            <div className="tooltip-title">Premium Feature</div>
                            <div className="tooltip-message">Message Center is available exclusively for Apex tier members and above.</div>
                            <div className="tooltip-action">Upgrade to unlock</div>
                          </div>
                          <div className="tooltip-arrow"></div>
                        </div>
                      </span>
                    )}
                    <Link to="/family-continuity-structure" className='nav-link'>Family Continuity Structure</Link>
                  </>
                ) : null}
                <Link to="/security" className="nav-link" onClick={() => setIsBurgerOpen(false)}>Security</Link>
              </>
            )
          )}
        </div>}
        <div className="nav-auth">
          {!isAuthenticated && !foundingCircle && (
            <>
              {/* Portal Access Dropdown */}
              <div className="nav-dropdown portal-access-dropdown" ref={portalAccessRef}>
                <button
                  className="nav-btn portal-access-btn"
                  onClick={() => setIsPortalAccessOpen(!isPortalAccessOpen)}
                  onBlur={() => setTimeout(() => setIsPortalAccessOpen(false), 200)}
                >
                  Portal Access
                  <span className={`dropdown-arrow ${isPortalAccessOpen ? 'open' : ''}`} style={{ marginLeft: '0.5rem' }}>▼</span>
                </button>
                {isPortalAccessOpen && (
                  <div className="dropdown-menu portal-access-menu open">
                    <button
                      className="dropdown-item"
                      onClick={() => {
                        setIsPortalAccessOpen(false);
                        if (isAuthenticated) {
                          navigate('/member-selection');
                        } else {
                          loginWithRedirect({ appState: { returnTo: '/member-selection' } });
                        }
                      }}
                    >
                      <span className="dropdown-item-icon">
                        <Home size={20} className="dropdown-item-icon-svg" />
                      </span>
                      <div>
                        <div className="dropdown-item-title">Family Portal</div>
                        <div className="dropdown-item-desc">Access family vaults</div>
                      </div>
                    </button>
                    <Link
                      to="/partner/families"
                      className="dropdown-item"
                      onClick={() => setIsPortalAccessOpen(false)}
                    >
                      <span className="dropdown-item-icon">
                        <Users2 size={20} className="dropdown-item-icon-svg" />
                      </span>
                      <div>
                        <div className="dropdown-item-title">Partner Family Portal</div>
                        <div className="dropdown-item-desc">Manage family vaults</div>
                      </div>
                    </Link>
                    <Link
                      to="/partner"
                      className="dropdown-item"
                      onClick={() => setIsPortalAccessOpen(false)}
                    >
                      <span className="dropdown-item-icon">
                        <Building2 size={20} className="dropdown-item-icon-svg" />
                      </span>
                      <div>
                        <div className="dropdown-item-title">Partner Portal</div>
                        <div className="dropdown-item-desc">Partner dashboard</div>
                      </div>
                    </Link>
                    {isLocal && (
                      <Link
                        to="/ev-outreach"
                        className="dropdown-item"
                        onClick={() => setIsPortalAccessOpen(false)}
                      >
                        <span className="dropdown-item-icon">
                          <Target size={20} className="dropdown-item-icon-svg" />
                        </span>
                        <div>
                          <div className="dropdown-item-title">Outreach Tracker</div>
                          <div className="dropdown-item-desc">Local only • SFO / Wealth / Legal</div>
                        </div>
                      </Link>
                    )}
                  </div>
                )}
              </div>

              <button
                className="nav-btn"
                onClick={handleSignupClick}
              >
                Request a Conversation
              </button>
            </>
          )}
          {isLoading ? null : isAuthenticated ? (
            <>
              <NotificationBell />
              <span className="nav-user">{user?.name || user?.email}</span>
              <button
                className="nav-btn"
                onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
              >
                Log Out
              </button>
            </>
          ) : null}
        </div>
      </nav>
    );
  }

  // Mobile navigation
  if (!isAuthenticated) {
    return (
      <>
        <nav className="nav-bar mobile-nav">
          <div className="mobile-nav-row">
            <button
              className="burger-menu-btn"
              aria-label="Open menu"
              onClick={() => setIsBurgerOpen(true)}
            >
              <FaBars size={28} />
            </button>
            <Link to="/" className="nav-logo">
              <img className='logo-img' src={logo} alt="Eterna Vaults Logo" />
            </Link>
            <div className="nav-auth mobile-auth">
              {/* <Link to="/how" className='nav-cta'>
              Start your vault
            </Link> */}
              <button
                className="nav-btn"
                onClick={handleSignupClick}
              >
                Request a Conversation
              </button>
              <button
                className="nav-btn"
                onClick={() => loginWithRedirect()}
              >
                Log In
              </button>
            </div>
          </div>
          {isBurgerOpen && (
            <div className="burger-menu-overlay">
              <aside className="burger-menu" ref={burgerMenuRef}>
                <button
                  className="burger-menu-close"
                  aria-label="Close menu"
                  onClick={() => setIsBurgerOpen(false)}
                >
                  <FaTimes size={28} />
                </button>
                <Link to="/why-eternavaults" className="burger-link" onClick={() => setIsBurgerOpen(false)}>Why Families Choose</Link>
                <Link to="/process" className="burger-link" onClick={() => setIsBurgerOpen(false)}>Continuity Protocol™</Link>
                <Link to="/case-studies" className="burger-link" onClick={() => setIsBurgerOpen(false)}>Case Studies</Link>
                <Link to="/institutional" className="burger-link" onClick={() => setIsBurgerOpen(false)}>Institutional Solutions</Link>
                <Link to="/for-advisors" className="burger-link" onClick={() => setIsBurgerOpen(false)}>For Advisors</Link>
                <Link to="/white-label" className="burger-link" onClick={() => setIsBurgerOpen(false)}>Custodied Infrastructure</Link>
                <Link to="/authority-decision-architecture" className="burger-link" onClick={() => setIsBurgerOpen(false)}>Authority & Decision Architecture™</Link>
                <Link to="/continuity-seismic-map" className="burger-link" onClick={() => setIsBurgerOpen(false)}>Continuity Seismic Map™</Link>
                <Link to="/glossary" className="burger-link" onClick={() => setIsBurgerOpen(false)}>Framework Glossary</Link>
                <Link to="/about" className="burger-link" onClick={() => setIsBurgerOpen(false)}>About EternaVaults</Link>
                <Link to="/how" className="burger-link" onClick={() => setIsBurgerOpen(false)}>How It Works</Link>
                <Link to="/emergency-access-model" className="burger-link" onClick={() => setIsBurgerOpen(false)}>Emergency Access Model</Link>
                <Link to="/authority-activation-flow" className="burger-link" onClick={() => setIsBurgerOpen(false)}>Authority Activation Flow™</Link>
                <Link to="/journal" className="burger-link" onClick={() => setIsBurgerOpen(false)}>The Legacy Journal</Link>
                <Link to="/archive" className="burger-link" onClick={() => setIsBurgerOpen(false)}>The Digital Heritage Archive</Link>
                <Link to="/founder-musings" className="burger-link" onClick={() => setIsBurgerOpen(false)}>Founder Musings</Link>
                <Link to="/family-legacy-guide" className="burger-link" onClick={() => setIsBurgerOpen(false)}>Family Legacy Guide</Link>
                <Link to="/press" className="burger-link" onClick={() => setIsBurgerOpen(false)}>Press & Media</Link>
                <Link to="/contact" className="burger-link" onClick={() => setIsBurgerOpen(false)}>Concierge Access</Link>
                <Link to="/security" className="burger-link" onClick={() => setIsBurgerOpen(false)}>Security</Link>
                {typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') && (
                  <Link to="/ev-outreach" className="burger-link" onClick={() => setIsBurgerOpen(false)}>Outreach Tracker (local)</Link>
                )}
              </aside>
            </div>
          )}
        </nav>

        {/* Mobile Signup Modal */}

        <MobileSignupModal
          isOpen={showMobileSignupModal}
          onClose={handleModalClose}
          onContinue={handleModalContinue}
        />
      </>
    );
  }

  // Mobile navigation for authenticated users (no bottom nav)
  return (
    <>
      {/* Top bar for mobile */}
      <nav className="nav-bar mobile-nav">
        <div className="mobile-nav-row">
          <button
            className="burger-menu-btn"
            aria-label="Open menu"
            onClick={() => setIsBurgerOpen(true)}
          >
            <FaBars size={28} />
          </button>
          <Link to="/" className="nav-logo">
            <img className='logo-img' src={logo} alt="Eterna Vaults Logo" />
          </Link>
          <NotificationBell />
          <span className="nav-user">{user?.name || user?.email}</span>
          <button
            className="nav-btn"
            onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
          >
            Log Out
          </button>
        </div>

        {isBurgerOpen && (
          <div className="burger-menu-overlay">
            <aside className="burger-menu" ref={burgerMenuRef}>
              <button
                className="burger-menu-close"
                aria-label="Close menu"
                onClick={() => setIsBurgerOpen(false)}
              >
                <FaTimes size={28} />
              </button>
              {/* Hide these links if user is a partner (partners use PartnerNavBar instead) */}
              {!isPartner && (
                <>
                  {hasActiveSubscription ? (
                    <>
                      <Link to="/member-selection" className="burger-link" onClick={() => setIsBurgerOpen(false)}>Access Vaults</Link>
                      {capabilities.messageCenter ? (
                        <Link to="/message-center" className="burger-link" onClick={() => setIsBurgerOpen(false)}>Message Center</Link>
                      ) : (
                        <span
                          className="burger-link burger-link-disabled"
                          style={{ opacity: 0.5, cursor: 'not-allowed', position: 'relative' }}
                          onClick={(e) => e.preventDefault()}
                        >
                          Message Center
                          <div className="premium-tooltip mobile-tooltip">
                            <div className="tooltip-icon">👑</div>
                            <div className="tooltip-content">
                              <div className="tooltip-title">Premium Feature</div>
                              <div className="tooltip-message">Message Center is available exclusively for Apex tier members and above.</div>
                              <div className="tooltip-action">Upgrade to unlock</div>
                            </div>
                            <div className="tooltip-arrow"></div>
                          </div>
                        </span>
                      )}
                      <Link to="/family-continuity-structure" className="burger-link" onClick={() => setIsBurgerOpen(false)}>Account Management</Link>
                    </>
                  ) : (
                    <a
                      href="/choose-plan"
                      className="burger-link"
                      onClick={(e) => {
                        e.preventDefault();
                        window.location.href = '/choose-plan';
                      }}
                    >
                      Choose Plan
                    </a>
                  )}
                  <Link to="/security" className="burger-link" onClick={() => setIsBurgerOpen(false)}>Security</Link>
                </>
              )}
            </aside>
          </div>
        )}
      </nav>

      <MobileSignupModal
        isOpen={showMobileSignupModal}
        onClose={handleModalClose}
        onContinue={handleModalContinue}
      />
    </>
  );
}
